import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCall } from './CallProvider';
import useMediaQuery from '../../utils/useMediaQuery';
import friendService from '../../services/friendService';
import { CALL_ICONS } from '../messages/CallRecordCard';
import { T, RADIUS, BODY_FONT } from '../messages/messagesModel';
import { statusLabel, routeLabel, groupTitle, ringOpacity, roomFor, QUICK_REPLIES, PERM_COPY, C, RING_GRADIENT, fmtTimer, snapCorner, cornerPosition, SNAP_MS, SNAP_EASE } from './callModel';
import { runCallPortal, ringStyle, reducedMotion } from './portalCallMotion';
import { onNativeCall } from '../../utils/nativeCall';

export const IC = {
  ...CALL_ICONS,
  mic: 'M9 3h6v11H9zM5 11a7 7 0 0 0 14 0M12 18v3',
  micOff: 'M9 3h6v11H9zM5 11a7 7 0 0 0 14 0M12 18v3M3 3l18 18',
  videoOff: 'M3 6h12v12H3zM15 10l6-3v10l-6-3zM2 2l20 20',
  speaker: 'M4 9h4l5-4v14l-5-4H4zM16 8a5 5 0 0 1 0 8M19 5a9 9 0 0 1 0 14',
  flip: 'M4 12a8 8 0 0 1 14-5l2 2M20 12a8 8 0 0 1-14 5l-2-2M20 4v5h-5M4 20v-5h5',
  end: 'M3 15c5-5 13-5 18 0l-3 3-3-2v-3a12 12 0 0 0-6 0v3l-3 2z',
  add: 'M10 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM3 21v-1a7 7 0 0 1 14 0v1M19 8v6M16 11h6',
  expand: 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5',
  lock: 'M4 11h16v10H4zM8 11V7a4 4 0 0 1 8 0v4',
  chevron: 'M6 9l6 6 6-6',
};

const Glyph = ({ d, size = 24, stroke = 2, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="square" style={style}><path d={d} /></svg>
);
const SCANLINES = 'repeating-linear-gradient(0deg, rgba(255,255,255,.05) 0 1px, transparent 1px 3px)';
const FULL_Z = 1150;

/**
 * Every view of the call that floats over the app (handoff Part B §5–7): the full screen
 * (the thread column on desktop, the whole screen on a phone) with its portal transition,
 * the incoming screen, the permission card, the floating video window and the desktop
 * voice card while minimized, and the remote audio that keeps playing through all of them.
 * The phone's voice call BAR lives in the app chrome (CallMiniBar), not here.
 */
export default function CallLayer() {
  const ctx = useCall();
  const phone = useMediaQuery('(max-width: 767px)');
  if (!ctx) return null;
  const { call } = ctx;
  return createPortal(
    // `display: contents` generates no box, so nothing moves — but pointer-events still
    // inherits through it, which is what takes the call UI back from a modal that disabled
    // the body (see .pr-overlay-root in index.css).
    <div className="pr-overlay-root" style={{ display: 'contents' }}>
      <RemoteAudio ctx={ctx} />
      <FullLayer ctx={ctx} phone={phone} />
      {call?.phase === 'perm' && <PermissionCard ctx={ctx} />}
      {call?.view === 'mini' && call.video && <FloatingWindow ctx={ctx} phone={phone} />}
      {call?.view === 'mini' && !call.video && !phone && <DesktopVoiceCard ctx={ctx} />}
      {ctx.toast && (
        <div role="status" className="font-mono font-bold" style={{ position: 'fixed', left: '50%', bottom: 'calc(120px + env(safe-area-inset-bottom, 0px))', transform: 'translateX(-50%)', zIndex: FULL_Z + 50, background: T.ink, color: T.white, borderRadius: RADIUS.pill, padding: '10px 16px', fontSize: 10, letterSpacing: '.1em', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
          {ctx.toast}
        </div>
      )}
    </div>,
    document.body,
  );
}

/** Every peer's audio, playing whatever view is up. Video elements draw the same streams. */
function RemoteAudio({ ctx }) {
  const { call, streams } = ctx;
  if (!call) return null;
  return (
    <div data-call-audio hidden={false} style={{ position: 'fixed', width: 0, height: 0, overflow: 'hidden' }}>
      {Array.from(streams.entries()).map(([peerId, stream]) => (
        <AudioSink key={`${call.id}:${peerId}`} stream={stream} speaker={call.speaker} />
      ))}
    </div>
  );
}

function AudioSink({ stream, speaker }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (el.srcObject !== stream) el.srcObject = stream;
    const play = () => { Promise.resolve(el.play?.()).catch(() => {}); };
    const resume = () => { if (document.visibilityState === 'visible') play(); };
    play();
    // Retry after a browser autoplay rejection or an interrupted mobile audio session.
    document.addEventListener('pointerup', play);
    document.addEventListener('keydown', play);
    document.addEventListener('visibilitychange', resume);
    stream.addEventListener?.('addtrack', play);
    // Answering on the lock screen and resuming after another phone call do not
    // produce a DOM gesture. Retry when CallKit actually grants us audio instead.
    const offNative = onNativeCall({ audioActive: (active) => { if (active) play(); } });
    return () => {
      offNative();
      document.removeEventListener('pointerup', play);
      document.removeEventListener('keydown', play);
      document.removeEventListener('visibilitychange', resume);
      stream.removeEventListener?.('addtrack', play);
      el.srcObject = null;
    };
  }, [stream]);
  // Speaker: where the browser lets us pick an output we do; elsewhere the OS owns it.
  useEffect(() => {
    const el = ref.current;
    if (!el?.setSinkId) return;
    el.setSinkId(speaker ? '' : '').catch(() => {});
  }, [speaker]);
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <audio ref={ref} autoPlay playsInline />;
}

/** A <video> bound to a MediaStream. */
export function StreamVideo({ stream, mirror = false, style, muted = true }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (stream && el.srcObject !== stream) el.srcObject = stream;
    if (!stream) el.srcObject = null;
    Promise.resolve(el.play?.()).catch(() => {});
  }, [stream]);
  // eslint-disable-next-line jsx-a11y/media-has-caption
  return <video ref={ref} autoPlay playsInline muted={muted} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: mirror ? 'scaleX(-1)' : undefined, ...style }} />;
}

/** The thread column's box on desktop (ThreadPane tags it data-call-anchor); null → the whole viewport. */
function useAnchorBox(phone, tick) {
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    if (phone) { setBox(null); return; }
    const el = document.querySelector('[data-call-anchor]');
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    if (r.width < 320 || r.height < 320) { setBox(null); return; }
    setBox({ left: r.left, top: r.top, width: r.width, height: r.height });
  }, [phone, tick]);
  return box;
}

/**
 * The full screen, with the portal: it opens out of the origin and, when the call ends or
 * minimizes, keeps the last frame while it closes back into it — END closes into the button
 * that started it, minimize into the mini view's corner.
 */
function FullLayer({ ctx, phone }) {
  const { call, origin } = ctx;
  const showing = !!call && call.view === 'full' && (['calling', 'ringing', 'connecting', 'live', 'incoming'].includes(call.phase) || (call.phase === 'perm' && (call.then === 'camera' || call.then === 'accept')));
  const [snapshot, setSnapshot] = useState(null);     // the last call while the close runs
  const layerRef = useRef(null);
  const ringRef = useRef(null);
  const wasShowing = useRef(false);
  const lastCall = useRef(null);
  const lastOrigin = useRef(null);
  const box = useAnchorBox(phone, call?.id);
  if (call) lastCall.current = call;
  // The frame the close runs on has to outlive the state that showed it: while the layer is
  // closing, the last call is what's drawn (frozen), until the portal has landed.
  const closingRef = useRef(false);
  const [, bump] = useState(0);
  if (!showing && wasShowing.current && !snapshot) closingRef.current = true;

  // Open: run the portal once the layer is in the DOM. Close: snapshot, run it back, clear.
  useLayoutEffect(() => {
    if (showing && !wasShowing.current) {
      wasShowing.current = true;
      lastOrigin.current = call?.dir === 'in' ? null : origin;
      const layer = layerRef.current;
      if (layer) {
        const o = lastOrigin.current && box ? { x: lastOrigin.current.x - box.left, y: lastOrigin.current.y - box.top } : lastOrigin.current;
        runCallPortal({ layer, ring: ringRef.current, origin: o, open: true });
      }
    } else if (!showing && wasShowing.current) {
      wasShowing.current = false;
      const last = lastCall.current;
      // Reduced motion (or no Web Animations): nothing to run, nothing to keep.
      if (reducedMotion() || !layerRef.current?.animate) { closingRef.current = false; bump((t) => t + 1); return; }
      // Minimize closes into the mini view's corner; END into where it started.
      const minimized = !!call && call.view === 'mini';
      const o = minimized ? miniOrigin(call, phone, box) : (lastOrigin.current && box ? { x: lastOrigin.current.x - box.left, y: lastOrigin.current.y - box.top } : lastOrigin.current);
      setSnapshot(last);
      runCallPortal({ layer: layerRef.current, ring: ringRef.current, origin: o, open: false }).then(() => { closingRef.current = false; setSnapshot(null); bump((t) => t + 1); });
    }
  }, [showing]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = showing ? call : (snapshot || (closingRef.current ? lastCall.current : null));
  if (!shown) return null;
  const frozen = !showing;
  const frame = box
    ? { position: 'fixed', left: box.left, top: box.top, width: box.width, height: box.height }
    : { position: 'fixed', inset: 0 };
  return (
    <div
      ref={layerRef}
      data-call-layer={shown.phase === 'incoming' || (shown.phase === 'perm' && shown.then === 'accept') ? 'in' : 'call'}
      data-testid="call-layer"
      role="dialog"
      aria-modal="true"
      aria-label={shown.phase === 'incoming' ? 'Incoming call' : 'Call'}
      style={{ ...frame, zIndex: FULL_Z, background: '#000', color: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'JetBrains Mono', ui-monospace, monospace", pointerEvents: frozen ? 'none' : 'auto' }}
    >
      {shown.phase === 'incoming' || (shown.phase === 'perm' && shown.then === 'accept')
        ? <IncomingScreen ctx={ctx} call={shown} frozen={frozen} />
        : <ActiveScreen ctx={ctx} call={shown} phone={phone} frozen={frozen} />}
      <div ref={ringRef} aria-hidden style={ringStyle} />
    </div>
  );
}

/** Where the mini view sits, in layer coordinates, for the close portal. */
function miniOrigin(call, phone, box) {
  if (phone && !call.video) return { x: 28, y: 86 };
  if (phone) return { x: call.miniPos?.corner?.endsWith('l') ? 72 : window.innerWidth - 72, y: call.miniPos?.corner?.startsWith('b') ? window.innerHeight - 200 : 200 };
  const w = box?.width || window.innerWidth; const h = box?.height || window.innerHeight;
  return { x: w - 190, y: h - 130 };
}

// ── Screens ──

function Avatar({ src, name, size, radius = '50%', style }) {
  return src
    ? <img src={src} alt="" style={{ width: size, height: size, borderRadius: radius, objectFit: 'cover', ...style }} />
    : <span style={{ width: size, height: size, borderRadius: radius, background: T.activeBadge, color: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: Math.round(size / 2.6), ...style }}>{(name || '?').charAt(0).toUpperCase()}</span>;
}

/** The 148px ring with the 128px avatar; the ring's opacity follows their level. */
function RingAvatar({ peer, level = 0, size = 148, inner = 128, bg = '#000' }) {
  return (
    <span style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: RING_GRADIENT, opacity: ringOpacity(level), transition: 'opacity 140ms linear' }} />
      <span style={{ position: 'absolute', inset: (size - inner) / 2 - 5, borderRadius: '50%', background: bg }} />
      <Avatar src={peer?.avatarUrl} name={peer?.name} size={inner} style={{ position: 'relative' }} />
    </span>
  );
}

function TopRow({ ctx, call, frozen }) {
  const live = call.phase === 'live';
  const btn = { width: 40, height: 40, borderRadius: '50%', border: '2px solid rgba(255,255,255,.35)', background: 'rgba(0,0,0,.3)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, cursor: 'pointer' };
  return (
    <div style={{ position: 'relative', zIndex: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 'calc(16px + env(safe-area-inset-top, 0px)) 16px 16px' }}>
      <button type="button" onClick={() => !frozen && ctx.minimize()} aria-label="Back to the app" data-testid="call-minimize" style={btn}>
        <Glyph d={IC.chevron} size={18} stroke={2.5} />
      </button>
      <span className="font-bold" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 9, letterSpacing: '.14em', color: 'rgba(255,255,255,.75)' }}>
        <Glyph d={IC.lock} size={10} stroke={3} />{routeLabel(call.route)}
      </span>
      <button type="button" onClick={() => !frozen && ctx.openAdd()} aria-label="Add people" style={{ ...btn, visibility: live ? 'visible' : 'hidden' }}>
        <Glyph d={IC.add} size={18} />
      </button>
    </div>
  );
}

function Controls({ ctx, call, frozen }) {
  const ctl = (label, icon, on, act, danger = false, testId) => ({ label, icon, on, act, danger, testId });
  const list = [
    ctl(call.muted ? 'UNMUTE' : 'MUTE', call.muted ? IC.micOff : IC.mic, call.muted, ctx.toggleMute, false, 'call-mute'),
    call.video
      ? ctl(call.camOn ? 'CAMERA' : 'CAMERA OFF', call.camOn ? IC.video : IC.videoOff, !call.camOn, ctx.toggleCamera, false, 'call-camera')
      : ctl('VIDEO', IC.video, false, ctx.toggleCamera, false, 'call-camera'),
    call.video
      ? ctl('FLIP', IC.flip, false, ctx.flipCamera, false, 'call-flip')
      : ctl('SPEAKER', IC.speaker, call.speaker, ctx.toggleSpeaker, false, 'call-speaker'),
    ctl('END', IC.end, false, ctx.hangup, true, 'call-end'),
  ];
  return (
    <div style={{ position: 'relative', zIndex: 3, display: 'flex', justifyContent: 'center', gap: 22, padding: '18px 16px calc(36px + env(safe-area-inset-bottom, 0px))', flexWrap: 'wrap', opacity: call.phase === 'ended' ? 0.35 : 1 }}>
      {list.map((c) => (
        <button key={c.label} type="button" onClick={() => !frozen && c.act()} aria-label={c.label} data-testid={c.testId} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, border: 0, background: 'none', padding: 0, color: '#fff', width: 64, cursor: 'pointer' }}>
          <span style={{ width: 60, height: 60, borderRadius: '50%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', border: c.danger ? 0 : '2px solid rgba(255,255,255,.35)', background: c.danger ? C.end : c.on ? '#fff' : 'rgba(0,0,0,.35)', color: c.danger ? '#fff' : c.on ? '#000' : '#fff', transition: 'background-color 200ms, color 200ms' }}>
            <Glyph d={c.icon} size={24} />
          </span>
          <span className="font-bold" style={{ fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.8)' }}>{c.label}</span>
        </button>
      ))}
    </div>
  );
}

function ActiveScreen({ ctx, call, phone, frozen }) {
  const { streams, localStream, now } = ctx;
  const live = call.phase === 'live';
  const group = call.peers.filter((p) => p.state !== 'left').length > 1;
  const one = call.peers.find((p) => p.state !== 'left') || call.peers[0];
  const remote = one ? streams.get(one.userId) : null;
  const remoteHasVideo = !!remote?.getVideoTracks().length && !one?.cameraPaused;
  const camLive = call.video && call.camOn && !!localStream?.getVideoTracks().some((t) => t.enabled);
  const status = statusLabel(call, now);
  const voiceLook = !call.video || group || (!live && !camLive);
  return (
    <>
      {call.video && !group && live && remoteHasVideo && <StreamVideo stream={remote} muted />}
      {call.video && !group && !live && camLive && (
        <>
          <StreamVideo stream={localStream} mirror muted />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.35)' }} />
        </>
      )}
      {call.video && !group && live && !remoteHasVideo && one && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
            <RingAvatar peer={one} level={one.level} />
            <span className="font-bold" style={{ fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.7)' }}>CAMERA OFF</span>
          </span>
        </div>
      )}
      {voiceLook && <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: SCANLINES }} />}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: 150, background: 'linear-gradient(to bottom, rgba(0,0,0,.7), rgba(0,0,0,0))', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 200, background: 'linear-gradient(to top, rgba(0,0,0,.75), rgba(0,0,0,0))', pointerEvents: 'none' }} />
      <TopRow ctx={ctx} call={call} frozen={frozen} />

      {call.video && !group && live && (
        <>
          <div style={{ position: 'relative', zIndex: 3, padding: '0 20px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className="font-bold uppercase" style={{ fontSize: 15, letterSpacing: '.08em', textShadow: '0 1px 6px rgba(0,0,0,.5)' }}>{one?.name}</span>
            <span style={{ fontSize: 11, letterSpacing: '.14em', color: 'rgba(255,255,255,.8)' }}>{status}</span>
          </div>
          <div style={{ position: 'absolute', right: 16, top: 'calc(72px + env(safe-area-inset-top, 0px))', zIndex: 4, width: 108, height: 144, borderRadius: 14, overflow: 'hidden', border: '2px solid #fff', background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {camLive ? <StreamVideo stream={localStream} mirror muted /> : (
              <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span className="font-bold" style={{ fontSize: 8, letterSpacing: '.12em', color: 'rgba(255,255,255,.7)' }}>CAMERA OFF</span>
              </span>
            )}
          </div>
        </>
      )}

      <div style={{ position: 'relative', zIndex: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '0 16px', textAlign: 'center' }}>
        {!group && (!call.video || !live) && one && (
          <>
            <RingAvatar peer={one} level={one.level} />
            <span className="font-bold uppercase" style={{ fontSize: 20, letterSpacing: '.08em' }}>{one.name}</span>
            <span data-testid="call-status" style={{ fontSize: 12, letterSpacing: '.14em', color: 'rgba(255,255,255,.75)' }}>{status}</span>
          </>
        )}
        {!group && call.video && live && <span data-testid="call-status" hidden>{status}</span>}
        {group && (
          <>
            <span className="font-bold" style={{ fontSize: 13, letterSpacing: '.12em' }}>{groupTitle(call)}</span>
            <span data-testid="call-status" style={{ fontSize: 11, letterSpacing: '.14em', color: 'rgba(255,255,255,.7)' }}>{status}</span>
            <div style={{ width: 'min(440px, 100%)', display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
              <GroupTile name="YOU" sub={call.muted ? ' · MUTED' : ''} avatar={ctx.me?.avatarUrl} level={call.selfLevel} video={call.video} stream={camLive ? localStream : null} mirror />
              {call.peers.filter((p) => p.state !== 'left').map((p) => (
                <GroupTile key={p.userId} name={(p.name || '').split(' ')[0].toUpperCase() || '…'} sub={p.state === 'ringing' ? ' · RINGING…' : p.muted ? ' · MUTED' : ''} avatar={p.avatarUrl} level={p.level} dim={p.state === 'ringing'} video={call.video} stream={!p.cameraPaused ? streams.get(p.userId) : null} speaking={p.level > 0.5} />
              ))}
            </div>
          </>
        )}
      </div>

      <Controls ctx={ctx} call={call} frozen={frozen} />
      {call.adding && !frozen && <AddToCallSheet ctx={ctx} call={call} />}
    </>
  );
}

function GroupTile({ name, sub, avatar, level = 0, dim = false, video = false, stream = null, mirror = false, speaking = false }) {
  const showVid = video && !!stream?.getVideoTracks?.().length;
  return (
    <div style={{ position: 'relative', aspectRatio: video ? '3 / 4' : '1 / 1', borderRadius: 18, overflow: 'hidden', background: '#141414', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, outline: speaking ? `3px solid ${C.speaker}` : '3px solid transparent', outlineOffset: -3, transition: 'outline-color 140ms' }}>
      {showVid ? <StreamVideo stream={stream} mirror={mirror} muted /> : (
        <span style={{ position: 'relative', width: 84, height: 84, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: RING_GRADIENT, opacity: dim ? 0.18 : ringOpacity(level), transition: 'opacity 140ms linear' }} />
          <span style={{ position: 'absolute', inset: 4, borderRadius: '50%', background: '#141414' }} />
          <Avatar src={avatar} name={name} size={70} style={{ position: 'relative', opacity: dim ? 0.5 : 1 }} />
        </span>
      )}
      <span className="font-bold" style={{ position: 'absolute', left: 10, bottom: 9, display: 'flex', alignItems: 'center', gap: 6, height: 22, padding: '0 8px', borderRadius: RADIUS.pill, background: 'rgba(0,0,0,.6)', fontSize: 9, letterSpacing: '.1em' }}>
        {name}<span style={{ color: 'rgba(255,255,255,.65)' }}>{sub}</span>
      </span>
    </div>
  );
}

function IncomingScreen({ ctx, call, frozen }) {
  const from = call.peers[0];
  const big = (bg) => ({ width: 72, height: 72, borderRadius: '50%', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' });
  return (
    <>
      {from?.avatarUrl && <img src={from.avatarUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.28 }} />}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: SCANLINES }} />
      <div className="font-bold" style={{ position: 'relative', padding: 'calc(20px + env(safe-area-inset-top, 0px)) 16px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, fontSize: 9, letterSpacing: '.14em', color: 'rgba(255,255,255,.7)' }}>
        <Glyph d={IC.lock} size={10} stroke={3} />ENCRYPTED · PEER-TO-PEER
      </div>
      <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, padding: '0 24px', textAlign: 'center' }}>
        <RingAvatar peer={from} level={0.3} />
        <span className="font-bold uppercase" style={{ fontSize: 20, letterSpacing: '.08em' }}>{from?.name}</span>
        <span data-testid="call-status" style={{ fontSize: 12, letterSpacing: '.14em', color: 'rgba(255,255,255,.75)' }}>{call.video ? 'INCOMING VIDEO CALL' : 'INCOMING VOICE CALL'}</span>
      </div>
      <div style={{ position: 'relative', display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', padding: '0 16px 22px' }}>
        {QUICK_REPLIES.map((t) => (
          <button key={t} type="button" onClick={() => !frozen && ctx.decline(t)} className="hover:bg-white hover:text-black" style={{ height: 34, padding: '0 14px', borderRadius: RADIUS.pill, border: '2px solid rgba(255,255,255,.4)', background: 'transparent', color: '#fff', fontFamily: BODY_FONT, fontSize: 13, cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}>{t}</button>
        ))}
      </div>
      <div style={{ position: 'relative', display: 'flex', justifyContent: 'center', gap: 72, padding: '6px 16px calc(40px + env(safe-area-inset-bottom, 0px))' }}>
        <button type="button" onClick={() => !frozen && ctx.decline()} data-testid="call-decline" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, border: 0, background: 'none', padding: 0, color: '#fff', cursor: 'pointer' }}>
          <span style={big(C.end)}><Glyph d={IC.end} size={28} /></span>
          <span className="font-bold" style={{ fontSize: 10, letterSpacing: '.12em' }}>DECLINE</span>
        </button>
        <button type="button" onClick={() => !frozen && ctx.accept()} data-testid="call-accept" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, border: 0, background: 'none', padding: 0, color: '#fff', cursor: 'pointer' }}>
          <span style={big(C.live)}><Glyph d={call.video ? IC.video : IC.phone} size={28} /></span>
          <span className="font-bold" style={{ fontSize: 10, letterSpacing: '.12em' }}>ACCEPT</span>
        </button>
      </div>
    </>
  );
}

/** First call only: our own card before the browser asks. */
function PermissionCard({ ctx }) {
  const { call } = ctx;
  const copy = call.then === 'camera' ? PERM_COPY.camOnly : call.video ? PERM_COPY.cam : PERM_COPY.mic;
  return (
    <div data-testid="call-permission" style={{ position: 'fixed', inset: 0, zIndex: FULL_Z + 10, background: 'rgba(0,0,0,.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
      <div style={{ width: 360, maxWidth: '100%', background: '#fff', borderRadius: 24, padding: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center', color: T.ink }}>
        <span style={{ width: 56, height: 56, borderRadius: '50%', background: T.ink, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Glyph d={call.video || call.then === 'camera' ? IC.video : IC.mic} size={22} />
        </span>
        <span className="font-bold" style={{ fontSize: 13, letterSpacing: '.14em' }}>{copy.title}</span>
        <span style={{ fontFamily: BODY_FONT, fontSize: 14, lineHeight: 1.45, color: T.grey500, textWrap: 'pretty' }}>{copy.body}</span>
        <button type="button" onClick={ctx.allowPerm} className="hover:bg-white hover:text-black" style={{ width: '100%', height: 44, borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, background: T.ink, color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: '.12em', cursor: 'pointer', transition: 'background-color 200ms, color 200ms' }}>{copy.cta}</button>
        <button type="button" onClick={ctx.denyPerm} style={{ border: 0, background: 'none', fontSize: 10, fontWeight: 700, letterSpacing: '.12em', color: T.grey500, padding: 4, cursor: 'pointer' }}>NOT NOW</button>
      </div>
    </div>
  );
}

/** ADD TO CALL: a white bottom sheet of friends not yet on the call. */
function AddToCallSheet({ ctx, call }) {
  const [people, setPeople] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await friendService.getFriends();
      if (cancelled) return;
      const list = r.success ? (r.data || []) : [];
      setPeople(list.map((f) => ({ userId: f.userId, name: f.displayName, avatarUrl: f.avatarUrl || null })));
    })();
    return () => { cancelled = true; };
  }, []);
  const on = new Set([ctx.myId, ...call.peers.map((p) => p.userId)]);
  const { line } = roomFor(call);
  const rows = (people || []).filter((p) => !on.has(p.userId));
  return (
    <>
      <div onClick={ctx.closeAdd} aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 5, background: 'rgba(0,0,0,.45)' }} />
      <div data-testid="call-add-sheet" style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 6, background: '#fff', color: T.ink, borderRadius: '24px 24px 0 0', padding: '18px 16px calc(24px + env(safe-area-inset-bottom, 0px))', display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '70%', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span className="font-bold" style={{ fontSize: 11, letterSpacing: '.12em' }}>ADD TO CALL</span>
          <span style={{ fontSize: 9, letterSpacing: '.12em', color: T.grey500 }}>{line}</span>
        </div>
        {people === null && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>…</span>}
        {people !== null && rows.length === 0 && <span style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey400 }}>EVERYONE YOU KNOW IS ON</span>}
        {rows.map((p) => (
          <button key={p.userId} type="button" onClick={() => ctx.addPeople([p])} className="hover:bg-[#F5F5F5]" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 8, margin: '0 -8px', border: 0, background: 'none', borderRadius: 14, textAlign: 'left', cursor: 'pointer' }}>
            <Avatar src={p.avatarUrl} name={p.name} size={40} radius={12} />
            <span className="font-bold" style={{ flex: 1, fontSize: 12, letterSpacing: '.08em' }}>{(p.name || '').toUpperCase()}</span>
            <span className="font-bold" style={{ height: 28, padding: '0 12px', borderRadius: RADIUS.pill, border: `2px solid ${T.ink}`, fontSize: 9, letterSpacing: '.1em', display: 'flex', alignItems: 'center' }}>ADD</span>
          </button>
        ))}
      </div>
    </>
  );
}

// ── Minimized ──

/** The phone's floating video window (1b) and the desktop one (1d): drag, snap to a corner, tap for controls. */
function FloatingWindow({ ctx, phone }) {
  const { call, streams, localStream, now } = ctx;
  const size = phone ? { width: 120, height: 168 } : { width: 340, height: 220 };
  const insets = phone ? { margin: 12, top: 64 + 48, bottom: 84 } : { margin: 24, top: 64, bottom: 0 };
  const box = { width: window.innerWidth, height: window.innerHeight };
  const [pos, setPos] = useState(() => cornerPosition(call.miniPos?.corner || 'tr', size, box, insets));
  const [drag, setDrag] = useState(false);
  const [open, setOpen] = useState(!phone);
  const g = useRef(null);
  const one = call.peers.find((p) => p.state !== 'left') || call.peers[0];
  const remote = one ? streams.get(one.userId) : null;
  const camLive = call.camOn && !!localStream?.getVideoTracks().some((t) => t.enabled);
  const onDown = (e) => { e.currentTarget.setPointerCapture?.(e.pointerId); g.current = { sx: e.clientX, sy: e.clientY, ox: pos.x, oy: pos.y, moved: false }; setDrag(true); };
  const onMove = (e) => {
    if (!g.current) return;
    const dx = e.clientX - g.current.sx; const dy = e.clientY - g.current.sy;
    if (Math.abs(dx) + Math.abs(dy) > 4) g.current.moved = true;
    setPos({ x: g.current.ox + dx, y: g.current.oy + dy });
  };
  const onUp = (e) => {
    // Controls stop pointer-down, so their pointer-up must not toggle the window.
    // Removing the phone controls here also removes the target before its click fires.
    if (!g.current) return;
    const moved = g.current.moved;
    g.current = null;
    setDrag(false);
    if (moved) {
      const snapped = snapCorner(pos, size, box, insets);
      setPos({ x: snapped.x, y: snapped.y });
      ctx.setCorner(snapped.corner);
    } else if (phone && e.type !== 'pointercancel') setOpen((o) => !o);
  };
  const stop = (e) => e.stopPropagation();
  const ctl = (label, icon, on, act, danger, testId) => (
    <button key={label} type="button" onPointerDown={stop} onClick={(e) => { e.stopPropagation(); act(); }} aria-label={label} data-testid={testId} style={{ width: phone ? 38 : 36, height: phone ? 38 : 36, borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: danger ? 0 : '2px solid rgba(255,255,255,.4)', background: danger ? C.end : on ? '#fff' : 'rgba(0,0,0,.4)', color: danger ? '#fff' : on ? '#000' : '#fff', cursor: 'pointer' }}>
      <Glyph d={icon} size={phone ? 16 : 15} />
    </button>
  );
  const controls = [
    ctl('Mute', call.muted ? IC.micOff : IC.mic, call.muted, ctx.toggleMute, false, 'mini-mute'),
    ctl('Camera', call.camOn ? IC.video : IC.videoOff, !call.camOn, ctx.toggleCamera, false, 'mini-camera'),
    ctl('Full call', IC.expand, false, () => ctx.restore(), false, 'mini-expand'),
    ctl('End', IC.end, false, ctx.hangup, true, 'mini-end'),
  ];
  return (
    <div
      data-testid="call-window"
      onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}
      style={{ position: 'fixed', left: pos.x, top: pos.y, width: size.width, height: size.height, zIndex: FULL_Z - 50, borderRadius: 16, overflow: 'hidden', border: phone ? '2px solid #fff' : '2px solid #000', boxShadow: '4px 4px 0 0 #000', background: '#000', touchAction: 'none', cursor: drag ? 'grabbing' : 'grab', transition: drag ? 'none' : `left ${SNAP_MS}ms ${SNAP_EASE}, top ${SNAP_MS}ms ${SNAP_EASE}`, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: '#fff' }}
    >
      {remote?.getVideoTracks().length && !one?.cameraPaused ? <StreamVideo stream={remote} muted style={{ pointerEvents: 'none' }} /> : (
        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Avatar src={one?.avatarUrl} name={one?.name} size={phone ? 44 : 72} radius={12} /></span>
      )}
      {camLive && <span style={{ position: 'absolute', ...(phone ? { right: 6, bottom: 6, width: 34, height: 46 } : { right: 10, top: 10, width: 72, height: 54 }), borderRadius: phone ? 8 : 9, overflow: 'hidden', border: '2px solid #fff', pointerEvents: 'none' }}><StreamVideo stream={localStream} mirror muted /></span>}
      <span className="font-bold" style={{ position: 'absolute', left: phone ? 6 : 10, top: phone ? 6 : 10, height: phone ? 18 : 22, padding: phone ? '0 6px' : '0 8px', borderRadius: RADIUS.pill, background: 'rgba(0,0,0,.65)', fontSize: phone ? 8 : 9, letterSpacing: '.1em', display: 'flex', alignItems: 'center', gap: phone ? 4 : 6, pointerEvents: 'none' }}>
        <span style={{ width: phone ? 5 : 6, height: phone ? 5 : 6, borderRadius: '50%', background: C.live }} />{phone ? fmtTimer(call.startedAt, now) : `${(one?.name || '').split(' ')[0].toUpperCase()} · ${fmtTimer(call.startedAt, now)}`}
      </span>
      {phone && open && (
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', display: 'grid', gridTemplateColumns: '1fr 1fr', placeItems: 'center', padding: '18px 10px' }}>{controls}</div>
      )}
      {!phone && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '24px 10px 10px', background: 'linear-gradient(to top, rgba(0,0,0,.7), rgba(0,0,0,0))', display: 'flex', justifyContent: 'center', gap: 8 }}>{controls}</div>
      )}
    </div>
  );
}

/** Desktop, voice: the black card bottom-right with the pink hard shadow. */
function DesktopVoiceCard({ ctx }) {
  const { call, now } = ctx;
  const one = call.peers.find((p) => p.state !== 'left') || call.peers[0];
  const ctl = (label, icon, on, act, danger, testId) => (
    <button key={label} type="button" onClick={act} aria-label={label} title={label} data-testid={testId} style={{ width: 34, height: 34, borderRadius: '50%', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', border: danger ? 0 : '2px solid rgba(255,255,255,.4)', background: danger ? C.end : on ? '#fff' : 'rgba(0,0,0,.4)', color: danger ? '#fff' : on ? '#000' : '#fff', cursor: 'pointer' }}>
      <Glyph d={icon} size={15} />
    </button>
  );
  return (
    <div data-testid="call-card" style={{ position: 'fixed', right: 24, bottom: 24, width: 340, zIndex: FULL_Z - 50, borderRadius: 18, background: '#000', color: '#fff', boxShadow: `4px 4px 0 0 ${C.cardShadow}`, padding: '12px 12px 12px 14px', display: 'flex', alignItems: 'center', gap: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
      <RingAvatar peer={one} level={one?.level} size={40} inner={34} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span className="font-bold uppercase truncate" style={{ fontSize: 11, letterSpacing: '.1em' }}>{call.peers.filter((p) => p.state !== 'left').length > 1 ? groupTitle(call) : one?.name}</span>
        <span style={{ fontSize: 9, letterSpacing: '.12em', color: 'rgba(255,255,255,.65)' }}>VOICE · {fmtTimer(call.startedAt, now)}</span>
      </span>
      {ctl('Mute', call.muted ? IC.micOff : IC.mic, call.muted, ctx.toggleMute, false, 'mini-mute')}
      {ctl('Turn on video', IC.video, false, ctx.toggleCamera, false, 'mini-camera')}
      {ctl('Back to full call', IC.expand, false, () => ctx.restore(), false, 'mini-expand')}
      {ctl('End', IC.end, false, ctx.hangup, true, 'mini-end')}
    </div>
  );
}
